import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId } from "../../../../../../redux/authSlice";
import {
  deactivateOrgLeadSource,
  listOrgLeadSources,
  upsertOrgLeadSource,
} from "../../../../../../services/organizationService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../../helpers/methods";
import YhButton from "../../../../../../ui/YhButton";
import YhInput from "../../../../../../ui/YhInput";
import YhModal from "../../../../../../ui/YhModal";
import YhSelect from "../../../../../../ui/YhSelect";

const SOURCE_GROUPS = [
  { label: "Social", value: "social" },
  { label: "Property portal", value: "property_portal" },
  { label: "Bulk upload", value: "bulk_upload" },
  { label: "Organic", value: "organic" },
  { label: "Offline", value: "offline" },
  { label: "Other", value: "other" },
];

const groupLabel = (g: string) =>
  SOURCE_GROUPS.find((x) => x.value === g)?.label || g || "Other";

const LeadSourceList: React.FC = () => {
  const activeOrganizationId = useSelector(selectActiveOrganizationId);
  const orgId =
    activeOrganizationId &&
    activeOrganizationId !== "all" &&
    String(activeOrganizationId).trim()
      ? String(activeOrganizationId)
      : "";

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [sourceGroup, setSourceGroup] = useState("other");

  const load = useCallback(async () => {
    if (!orgId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await listOrgLeadSources(orgId, { all: true });
      const data = res?.data?.items || res?.data || res?.items || [];
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load lead sources");
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setName("");
    setSourceGroup("other");
    setOpen(true);
  };

  const handleSave = async () => {
    if (!orgId) {
      openErrorNotification("Select an organization first");
      return;
    }
    if (!name.trim()) {
      openErrorNotification("Source name is required");
      return;
    }
    setSaving(true);
    try {
      await upsertOrgLeadSource(orgId, {
        source_name: name.trim(),
        sourceGroup,
      });
      openSuccessNotification("Lead source saved");
      setOpen(false);
      load();
    } catch (e: any) {
      openErrorNotification(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (row: any) => {
    if (!orgId || !row?._id) return;
    if (!window.confirm(`Deactivate source "${row.source_name}"?`)) return;
    try {
      await deactivateOrgLeadSource(orgId, String(row._id));
      openSuccessNotification("Source deactivated");
      load();
    } catch (e: any) {
      openErrorNotification(e?.message || "Deactivate failed");
    }
  };

  const handleActivate = async (row: any) => {
    if (!orgId) return;
    try {
      await upsertOrgLeadSource(orgId, {
        source_name: row.source_name,
        sourceGroup: row.sourceGroup || "other",
      });
      openSuccessNotification("Source activated");
      load();
    } catch (e: any) {
      openErrorNotification(e?.message || "Activate failed");
    }
  };

  const isActive = (row: any) =>
    row.isActive !== false && String(row.status) !== "0";

  return (
    <div className="box-border w-full space-y-5 px-4 py-4 md:px-6 md:py-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#111827]">
            Lead Sources
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Organization-level source master used on leads, bulk upload, forms,
            and reports. Select an organization in the header to manage sources.
          </p>
        </div>
        <YhButton
          variant="primary"
          size="sm"
          onClick={openCreate}
          disabled={!orgId}
        >
          Add Source
        </YhButton>
      </div>

      {!orgId ? (
        <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
          Select a specific organization (not “All”) to view and manage lead
          sources.
        </div>
      ) : null}

      <div className="w-full overflow-x-auto rounded-xl border border-[#E5E7EB] bg-white">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-[#F9FAFB] text-[#6B7280]">
            <tr>
              <th className="px-4 py-3 font-medium">Source name</th>
              <th className="px-4 py-3 font-medium">Group</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!orgId ? (
              <tr>
                <td className="px-4 py-8 text-[#6B7280]" colSpan={4}>
                  No organization selected.
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td className="px-4 py-8 text-[#6B7280]" colSpan={4}>
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td className="px-4 py-8 text-[#6B7280]" colSpan={4}>
                  No lead sources yet. Add one to use on forms and leads.
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row._id} className="border-t border-[#F3F4F6]">
                  <td className="px-4 py-3 font-medium text-[#111827]">
                    {row.source_name}
                  </td>
                  <td className="px-4 py-3 text-[#6B7280]">
                    {groupLabel(row.sourceGroup)}
                  </td>
                  <td className="px-4 py-3">
                    {isActive(row) ? "Active" : "Inactive"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {isActive(row) ? (
                        <YhButton
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeactivate(row)}
                        >
                          Deactivate
                        </YhButton>
                      ) : (
                        <YhButton
                          variant="secondary"
                          size="sm"
                          onClick={() => handleActivate(row)}
                        >
                          Activate
                        </YhButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <YhModal
        open={open}
        onClose={() => !saving && setOpen(false)}
        title="Add Lead Source"
        width={440}
        footer={
          <div className="flex justify-end gap-2">
            <YhButton
              variant="secondary"
              size="sm"
              disabled={saving}
              onClick={() => setOpen(false)}
            >
              Cancel
            </YhButton>
            <YhButton
              variant="primary"
              size="sm"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save"}
            </YhButton>
          </div>
        }
      >
        <div className="space-y-3">
          <YhInput
            label="Source name *"
            value={name}
            onChange={(e: any) => setName(e.target?.value ?? e)}
            placeholder="e.g. Facebook Ads, 99acres, Walk-in"
          />
          <YhSelect
            label="Group"
            value={sourceGroup}
            onChange={(e: any) =>
              setSourceGroup(String(e?.target?.value || "other"))
            }
            options={SOURCE_GROUPS}
          />
        </div>
      </YhModal>
    </div>
  );
};

export default LeadSourceList;
